import React, { useCallback, useState } from 'react';

import superagent from 'superagent';

import { ServerResponseError, ServerSolarLandingDto, SolarLandingSubmissionsData } from '../types';

import { useMessage } from '@ott/message';

import * as ReportsToast from '../../blocks/ReportsToast';

const TOAST_CONFIG = {
  duration: 2000,
  className: ReportsToast.RootClassName,
  childrenClassNames: {
    container: ReportsToast.ContainerClassName,
    iconWrapper: ReportsToast.IconClassName,
  },
};

type SolarLandingSubmissionsMutationProps = {
  onSuccess?: (data: ServerSolarLandingDto['result']) => void;
  onError?: (error: ServerSolarLandingDto['error']) => void;
};

export const useSolarLandingSubmissionsMutation = ({
  onSuccess,
  onError,
}: SolarLandingSubmissionsMutationProps) => {
  const [isLoading, setLoading] = useState(false);

  const messageApi = useMessage();

  const showSuccessToast = useCallback(() => {
    messageApi.success(
      <ReportsToast.Content>
        <ReportsToast.Text>You’re all set!</ReportsToast.Text>
        <ReportsToast.Text>Please check your inbox for the details.</ReportsToast.Text>
      </ReportsToast.Content>,
      TOAST_CONFIG
    );
  }, [messageApi]);

  const showDangerToast = useCallback(() => {
    messageApi.error(
      <ReportsToast.Content>
        <ReportsToast.Text>Something went wrong.</ReportsToast.Text>
        <ReportsToast.Text>Please wait a few minutes and try again.</ReportsToast.Text>
      </ReportsToast.Content>,
      TOAST_CONFIG
    );
  }, [messageApi]);

  const mutate = useCallback(
    async (data: SolarLandingSubmissionsData) => {
      setLoading(true);
      try {
        const response = await superagent.post('/_mark/landingSubmissions/solarReport').send(data);

        const body = response.body as ServerSolarLandingDto;

        if (body.result.success) {
          showSuccessToast();
          onSuccess?.(body.result);
          return;
        }

        if (body.error) {
          showDangerToast();
          onError?.(body.error);
        }
      } catch (error) {
        console.error(error);

        const responseError = (error as ServerResponseError)?.response?.body?.error;
        showDangerToast();
        onError?.(responseError);
      } finally {
        setLoading(false);
      }
    },
    [showSuccessToast, showDangerToast, onSuccess, onError]
  );

  return {
    isLoading,
    mutate,
  };
};
